import { Shield, ShieldCheck, ShieldAlert } from "lucide-react";
import { clsx } from "clsx";
import type { E2EEStatus } from "../lib/e2ee";
import { Button } from "./ui";

export function SecurityPanel({
  status,
  remoteFingerprints,
  onVerify,
  verified,
  liveKit,
}: {
  status: E2EEStatus;
  remoteFingerprints: { peerId: string; fingerprint: string }[];
  onVerify: () => void;
  verified: boolean;
  liveKit?: boolean;
}) {
  return (
    <div className="glass-panel rounded-2xl p-4 text-sm">
      <div className="mb-3 flex items-center gap-2">
        {verified ? (
          <ShieldCheck className="h-5 w-5 text-accent-400" />
        ) : status.enabled ? (
          <ShieldAlert className="h-5 w-5 text-amber-300" />
        ) : (
          <Shield className="h-5 w-5 text-ink-500" />
        )}
        <div>
          <p className="font-display font-semibold">Encryption</p>
          <p className="text-xs text-ink-500">
            {status.enabled ? "E2EE enabled for this room" : "Standard WebRTC transport only"}
          </p>
        </div>
      </div>

      <ul className="space-y-2 text-xs text-ink-300">
        <li>
          <span className="text-ink-500">Media:</span>{" "}
          {liveKit
            ? "LiveKit SFU (DTLS-SRTP to server; server routes media — not mesh P2P)"
            : status.mediaLayer === "insertable-streams"
              ? "DTLS-SRTP + insertable stream AES-GCM"
              : "DTLS-SRTP (peer-to-peer keys; server cannot decrypt media)"}
        </li>
        <li>
          <span className="text-ink-500">Chat:</span>{" "}
          {liveKit
            ? "TLS to server (not end-to-end encrypted)"
            : status.chatEncrypted
              ? "AES-256-GCM (server stores ciphertext only)"
              : "Standard TLS"}
        </li>
        <li>
          <span className="text-ink-500">TURN:</span> Uses configured ICE/TURN for connectivity (transport TLS)
        </li>
      </ul>

      {status.securityCode && (
        <div className="mt-3 rounded-xl bg-surface-950/80 p-3 ring-1 ring-white/10">
          <p className="text-[10px] uppercase tracking-widest text-ink-500">Your security code</p>
          <p className="mt-1 font-mono text-xs text-accent-400">{status.securityCode}</p>
          <p className="mt-2 text-[11px] text-ink-500">
            Compare this code with other participants (voice or trusted channel). Matching codes mean the same
            encryption key.
          </p>
        </div>
      )}

      {remoteFingerprints.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-[10px] uppercase tracking-widest text-ink-500">Participants</p>
          {remoteFingerprints.map((f) => (
            <p key={f.peerId} className="font-mono text-[11px] text-ink-400">
              {f.fingerprint}
            </p>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant={verified ? "primary" : "ghost"}
        className={clsx("mt-3 w-full", verified && "pointer-events-none opacity-80")}
        onClick={onVerify}
        disabled={!status.ready}
      >
        {verified ? "I verified the security code" : "Mark code as verified"}
      </Button>
    </div>
  );
}
